<script setup lang="ts">
import type { OpeningHours } from '~/types/api'
import type { DayName } from '~/utils/utc-date'
import { getCurrentWeek, DAY_LABELS, isDateInCurrentWeek } from '~/utils/utc-date'

type ExplicitOpeningHours = WithRequired<OpeningHours, 'validFrom' | 'validThrough'>
type ImplicitOpeningHours = WithRequired<OpeningHours, 'dayOfWeek'>

interface VOpeningHours {
    hours: OpeningHours[]
}

const props = defineProps<VOpeningHours>()

const currentWeek = getCurrentWeek()

const implicitHours = computed(() => {
    return props.hours.filter(h => !!h?.dayOfWeek) as ImplicitOpeningHours[]
})

const explicitHours = computed(() => {
    const filteredHours = props.hours.filter((hour) => {
        if (!hour?.validFrom || !hour?.validThrough) return false

        return isDateInCurrentWeek(hour.validFrom) || isDateInCurrentWeek(hour.validThrough)
    }) as ExplicitOpeningHours[]

    return filteredHours.map((hour) => {
        const startDayIndex = new Date(hour.validFrom).getDay()
        const endDayIndex = new Date(hour.validThrough).getDay()

        // Shift day index on sunday
        const dayOfWeek = DAY_LABELS.slice(
            (startDayIndex === 0 ? 7 : startDayIndex) - 1,
            endDayIndex === 0 ? 7 : endDayIndex,
        )
        return {
            ...hour,
            dayOfWeek,
        }
    })
})

const isDayIncluded = (day: DayName, days: OpeningHours['dayOfWeek']) => {
    if (typeof days === 'string') return day === days
    return days?.some(schemaDay => schemaDay.includes(day) || schemaDay === day)
}

function getHoursByDay(day: DayName, hours: OpeningHours[]) {
    const openingHours = hours.filter(hour => isDayIncluded(day, hour.dayOfWeek))

    return openingHours.map((hour) => {
        if (hour.opens === hour.closes) return []
        return [hour.opens, hour.closes]
    })
}

const { t } = useI18n()
const week = computed(() => {
    return currentWeek.map((day) => {
        const explicitRangeHours = getHoursByDay(day.label, explicitHours.value)
        const implicitRangeHours = getHoursByDay(day.label, implicitHours.value)

        const rangeHours = explicitRangeHours.length ? explicitRangeHours : implicitRangeHours

        const dayRange = rangeHours
            .map((range) => {
                return t('agency.hour_range', {
                    count: range.length,
                    open: range[0],
                    close: range[1],
                })
            })
            .sort((a, b) => {
                return parseFloat(a.substring(0, 2)) - parseFloat(b.substring(0, 2))
            })

        const content = t('agency.day_range', {
            count: dayRange.length,
            firstRange: dayRange[0],
            secondRange: dayRange[1],
        })

        return {
            isExplicit: !!explicitRangeHours.length,
            label: new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'long' }),
            content,
        }
    })
})
</script>

<template>
    <div
        :class="$style.root"
        class="text-body-m"
    >
        <div
            v-for="day in week"
            :key="day.label"
        >
            <span
                :class="$style.day"
                class="bold"
            >{{ day.label }}:</span>
            <span>{{ day.content }}
                <template v-if="day.isExplicit">({{ $t('agency.hours_explicit') }})</template>
            </span>
        </div>
    </div>
</template>

<style lang="scss" module>
.root {
    position: relative;
}

.day {
    margin-right: 0.5ch;
}

.hour {
    margin-right: 1ch;
}
</style>
